import {array, InferType, number, object, string} from 'yup';

import {currencyValueDivider} from '@shared/constants';

const amountSchema = object({
  currency: string()
    .matches(/^[A-Z]{3}$/, 'Select a currency')
    .required('Currency is required'),
  amount: number()
    .transform((value: number, original: unknown) =>
      original === '' ? undefined : value,
    )
    .typeError('Enter a valid amount')
    .min(0.01, 'Minimum amount is 0.01')
    .required('Amount is required')
    .test('precision', 'Use at most two decimal places', value =>
      /^\d+(\.\d{1,2})?$/.test(String(value)),
    )
    .test('range', 'Amount is too large', value =>
      Number.isSafeInteger(Math.round(value * currencyValueDivider)),
    ),
});

export const voucherConfigFormSchema = object({
  name: object({
    en: string().trim().defined(),
    es: string().trim().defined(),
    pt: string().trim().defined(),
    tl: string().trim().defined(),
  }).test('required', 'Name is required', function (value) {
    return (
      Object.values(value).some(text => text !== '') ||
      this.createError({path: `${this.path}.en`, message: 'Name is required'})
    );
  }),
  amounts: array()
    .of(amountSchema)
    .required()
    .min(1, 'Add at least one currency')
    .test('usd', 'USD amount is required', values =>
      values.some(value => value.currency === 'USD'),
    )
    .test(
      'unique',
      'Each currency can only be added once',
      values =>
        new Set(values.map(value => value.currency)).size === values.length,
    ),
  ttlDays: number()
    .transform((value: number, original: unknown) =>
      original === '' ? undefined : value,
    )
    .typeError('Enter a valid number of days')
    .integer('Use a whole number of days')
    .positive('Lifetime must be at least one day')
    .max(Number.MAX_SAFE_INTEGER)
    .required('Lifetime is required'),
});

export type VoucherConfigFormValues = InferType<typeof voucherConfigFormSchema>;
